import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { useFavorites } from "@/contexts/FavoritesContext";
import { ShoppingBag, Heart, User, Menu, X, Search, ChevronDown, Instagram, MapPin } from "lucide-react";
import api, { BACKEND_URL } from "@/lib/api";

const PARENT_SLUGS = ["saat", "gumus", "altin"]; // order in nav: SAAT, GÜMÜŞ, ALTIN

export default function Layout({ children }) {
  const { t, lang, setLang } = useLang();
  const { user } = useAuth();
  const { count } = useCart();
  const { count: favCount } = useFavorites();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openSub, setOpenSub] = useState(null);
  const [site, setSite] = useState(null);
  const [categories, setCategories] = useState([]);
  const wsRef = useRef(null);
  const loc = useLocation();

  const isAdmin = user && ["super_admin", "admin", "content_manager", "order_manager"].includes(user.role);

  useEffect(() => { setMenuOpen(false); setOpenSub(null); }, [loc.pathname]);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  const fetchSite = () => api.get("/settings/site").then((r) => setSite(r.data)).catch(() => {});
  const fetchCategories = () => api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});

  useEffect(() => { fetchSite(); fetchCategories(); }, []);

  // Dynamically update favicon when favicon URL changes (independent of logo)
  useEffect(() => {
    const favUrl = site?.favicon_url;
    if (!favUrl) return;
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = favUrl;
  }, [site?.favicon_url]);

  // Real-time updates via WebSocket
  useEffect(() => {
    if (!BACKEND_URL) return;
    const wsUrl = BACKEND_URL.replace(/^http/, "ws") + "/api/ws/rates";
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === "categories_changed") fetchCategories();
          if (msg.type === "site_updated") fetchSite();
        } catch {}
      };
      return () => { try { ws.close(); } catch {} };
    } catch {}
  }, []);

  const instagramUrl = site?.instagram_url || "https://www.instagram.com/uzungolkuyumculuk/";
  const phone = site?.phone || "+90 536 814 7467";

  // Group categories by parent slug
  const parentMap = {};
  categories.forEach((c) => { if (!c.parent_id) parentMap[c.slug] = c; });
  const childrenByParentId = {};
  categories.forEach((c) => {
    if (c.parent_id) {
      childrenByParentId[c.parent_id] = childrenByParentId[c.parent_id] || [];
      childrenByParentId[c.parent_id].push(c);
    }
  });
  Object.values(childrenByParentId).forEach((arr) => arr.sort((a, b) => (a.order || 0) - (b.order || 0)));

  const closeAll = () => { setOpenSub(null); setMenuOpen(false); };

  return (
    <div className="min-h-screen flex flex-col bg-bone">
      <header data-testid="site-header" className={`sticky top-0 z-40 transition-all duration-300 ${scrolled ? "backdrop-blur-xl bg-bone/90 border-b border-border/60" : "bg-bone"}`}>
        <div className="max-w-[1440px] mx-auto px-4 lg:px-10">
          <div className="flex items-center justify-between h-20 gap-4">
            {/* Brand — logo + text. Allowed to shrink on mobile so the right-cluster never overflows. */}
            <Link data-testid="logo-link" to="/" onClick={closeAll} className="flex items-center gap-2 sm:gap-3 group min-w-0 flex-1 lg:flex-none">
              {site?.logo_url && (
                <img src={site.logo_url} alt="Uzungöl Kuyumculuk" className="h-9 w-9 sm:h-10 sm:w-10 md:h-12 md:w-12 lg:h-14 lg:w-14 object-contain shrink-0" />
              )}
              <span className="flex flex-col items-start leading-none min-w-0">
                <span className="font-heading text-base sm:text-xl md:text-2xl lg:text-3xl font-semibold tracking-tight text-[#222222] group-hover:text-[#C8A96A] transition-colors truncate max-w-full">Uzungöl Kuyumculuk</span>
                <span className="hidden sm:inline text-[10px] uppercase tracking-[0.32em] text-[#C8A96A] mt-1 font-body truncate max-w-full">HANDMADE JEWELRY</span>
              </span>
            </Link>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-1 ml-6" onMouseLeave={() => setOpenSub(null)}>
              {PARENT_SLUGS.map((slug) => {
                const parent = parentMap[slug];
                if (!parent) return null;
                const kids = childrenByParentId[parent.id] || [];
                const label = (lang === "tr" ? parent.name_tr : parent.name_en).toUpperCase();
                return (
                  <SubNav key={slug} id={slug} label={label} parentTo={`/products?category=${parent.id}`} open={openSub} setOpen={setOpenSub}>
                    {kids.length === 0 ? (
                      <Link to={`/products?category=${parent.id}`} className="block px-5 py-3 text-sm hover:bg-[#F5F2EC] hover:text-[#C8A96A] transition-colors">
                        {lang === "tr" ? `Tüm ${parent.name_tr}` : `All ${parent.name_en}`}
                      </Link>
                    ) : (
                      <>
                        <Link to={`/products?category=${parent.id}`} data-testid={`subnav-all-${slug}`}
                          className="block px-5 py-3 text-sm font-medium border-b border-border hover:bg-[#F5F2EC] hover:text-[#C8A96A] transition-colors">
                          {lang === "tr" ? `Tüm ${parent.name_tr}` : `All ${parent.name_en}`}
                        </Link>
                        {kids.map((k) => (
                          <Link key={k.id}
                            to={`/products?category=${k.id}`}
                            data-testid={`subnav-${k.slug}`}
                            className="block px-5 py-3 text-sm hover:bg-[#F5F2EC] hover:text-[#C8A96A] transition-colors">
                            {lang === "tr" ? k.name_tr : k.name_en}
                          </Link>
                        ))}
                      </>
                    )}
                  </SubNav>
                );
              })}
            </nav>

            <div className="flex items-center gap-3 lg:gap-4 shrink-0">
              <button data-testid="lang-toggle" onClick={() => setLang(lang === "tr" ? "en" : "tr")}
                className="text-xs font-medium uppercase tracking-wider hover:text-[#C8A96A] transition-colors">
                {lang === "tr" ? "EN" : "TR"}
              </button>
              <a href={instagramUrl} target="_blank" rel="noopener noreferrer" data-testid="nav-instagram" aria-label="Instagram"
                className="hidden sm:inline-block hover:text-[#C8A96A] transition-colors">
                <Instagram size={18} />
              </a>
              <Link to={user ? "/account" : "/login"} data-testid={user ? "nav-account" : "nav-login"}><User size={18} className="hover:text-[#C8A96A] transition-colors" /></Link>
              <Link to="/favorites" data-testid="nav-favorites" aria-label="Favorites" className="relative hidden sm:inline-block">
                <Heart size={18} className="hover:text-[#C8A96A] transition-colors" />
                {favCount > 0 && (
                  <span data-testid="fav-count-badge" className="absolute -top-2 -right-2 bg-[#C8A96A] text-white text-[10px] min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full font-medium">{favCount}</span>
                )}
              </Link>
              <Link to="/cart" data-testid="nav-cart" className="relative">
                <ShoppingBag size={18} className="hover:text-[#C8A96A] transition-colors" />
                {count > 0 && (
                  <span data-testid="cart-count-badge" className="absolute -top-2 -right-2 bg-[#C8A96A] text-white text-[10px] w-4 h-4 flex items-center justify-center rounded-full font-medium">{count}</span>
                )}
              </Link>
              {isAdmin && (
                <Link to="/admin" data-testid="nav-admin" className="hidden md:inline-block text-[10px] uppercase tracking-[0.2em] text-[#C8A96A] border border-[#C8A96A] px-3 py-1 hover:bg-[#C8A96A] hover:text-white transition-colors">
                  {lang === "tr" ? "Yönetim" : "Admin"}
                </Link>
              )}
              <button data-testid="mobile-menu-btn" className="lg:hidden ml-1" onClick={() => setMenuOpen(!menuOpen)} aria-label="menu">
                {menuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>

          {/* Mobile menu */}
          {menuOpen && (
            <div data-testid="mobile-nav" className="lg:hidden border-t border-border/40 py-4 space-y-1 max-w-full overflow-x-hidden">
              {site?.logo_url && (
                <div className="flex items-center gap-3 pb-3 mb-2 border-b border-border/40">
                  <img src={site.logo_url} alt="Uzungöl" className="h-10 w-10 object-contain shrink-0" />
                  <span className="font-heading text-lg truncate">Uzungöl Kuyumculuk</span>
                </div>
              )}
              {PARENT_SLUGS.map((slug) => {
                const parent = parentMap[slug];
                if (!parent) return null;
                const kids = childrenByParentId[parent.id] || [];
                return (
                  <MobileSection key={slug} label={(lang === "tr" ? parent.name_tr : parent.name_en).toUpperCase()}>
                    {kids.map((k) => (
                      <Link key={k.id} to={`/products?category=${k.id}`} className="block py-2 pl-4 text-sm text-[#52525B]">
                        {lang === "tr" ? k.name_tr : k.name_en}
                      </Link>
                    ))}
                  </MobileSection>
                );
              })}

              <div className="border-t border-border/40 mt-3 pt-3 space-y-1">
                <Link
                  to={user ? "/account" : "/login"}
                  data-testid="mobile-account-link"
                  className="flex items-center gap-3 py-3 text-sm uppercase tracking-[0.15em] text-[#0A0A0A]"
                >
                  <User size={14} /> {user ? (lang === "tr" ? "Hesabım" : "My Account") : (lang === "tr" ? "Giriş Yap" : "Sign In")}
                </Link>

                <Link
                  to="/favorites"
                  data-testid="mobile-favorites-link"
                  className="flex items-center gap-3 py-3 text-sm uppercase tracking-[0.15em] text-[#0A0A0A]"
                >
                  <Heart size={14} /> {lang === "tr" ? "Favorilerim" : "Favorites"}
                  {favCount > 0 && (
                    <span className="ml-auto bg-[#C8A96A] text-white text-[10px] min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full font-medium">{favCount}</span>
                  )}
                </Link>

                <button
                  data-testid="mobile-lang-toggle"
                  onClick={() => setLang(lang === "tr" ? "en" : "tr")}
                  className="flex items-center gap-3 py-3 text-sm uppercase tracking-[0.15em] text-[#0A0A0A] w-full text-left"
                >
                  <span className="text-[10px] font-medium border border-current px-1.5 py-0.5">{lang === "tr" ? "EN" : "TR"}</span>
                  {lang === "tr" ? "English" : "Türkçe"}
                </button>

                <a
                  href={instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="mobile-instagram-link"
                  className="flex items-center gap-3 py-3 text-sm uppercase tracking-[0.15em] text-[#C8A96A]"
                >
                  <Instagram size={14} /> Instagram
                </a>

                {isAdmin && (
                  <Link to="/admin" className="block py-3 text-sm uppercase tracking-[0.15em] text-[#C8A96A]">
                    {lang === "tr" ? "Yönetim Paneli" : "Admin Panel"}
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer data-testid="site-footer" className="bg-[#0A0A0A] text-[#E5E4DF] mt-24">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-16 grid md:grid-cols-4 gap-10">
          <div>
            <div className="flex items-center gap-3 mb-2">
              {site?.logo_url && <img src={site.logo_url} alt="Uzungöl" className="h-10 w-10 object-contain" />}
              <h3 className="font-heading text-2xl">Uzungöl Kuyumculuk</h3>
            </div>
            <p className="text-[10px] uppercase tracking-[0.32em] text-[#C8A96A] mb-4">Handmade Jewelry</p>
            <p className="text-sm text-[#A1A1AA] leading-relaxed">{lang === "tr" ? "2010'dan beri Çaykara'da el işçiliği mücevher." : "Hand-crafted jewelry in Çaykara since 2010."}</p>
            <a href={instagramUrl} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram"
              className="inline-flex items-center gap-2 mt-6 text-xs uppercase tracking-[0.2em] text-[#C8A96A] hover:text-white transition-colors">
              <Instagram size={14} /> @uzungolkuyumculuk
            </a>
          </div>
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] mb-4 text-[#C8A96A]">{lang === "tr" ? "Koleksiyonlar" : "Collections"}</h4>
            <ul className="space-y-2 text-sm text-[#A1A1AA]">
              {PARENT_SLUGS.map((slug) => {
                const p = parentMap[slug];
                if (!p) return null;
                return <li key={slug}><Link to={`/categories`} className="hover:text-white">{lang === "tr" ? p.name_tr : p.name_en}</Link></li>;
              })}
              <li><Link to="/categories" className="hover:text-white">{lang === "tr" ? "Tüm Kategoriler" : "All Categories"}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] mb-4 text-[#C8A96A]">{lang === "tr" ? "Hesap" : "Account"}</h4>
            <ul className="space-y-2 text-sm text-[#A1A1AA]">
              <li><Link to="/account" className="hover:text-white">{lang === "tr" ? "Hesabım" : "My Account"}</Link></li>
              <li><Link to="/cart" className="hover:text-white">{lang === "tr" ? "Sepet" : "Cart"}</Link></li>
              <li><Link to="/login" className="hover:text-white">{lang === "tr" ? "Giriş Yap" : "Sign In"}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] mb-4 text-[#C8A96A]">{lang === "tr" ? "İletişim" : "Contact"}</h4>
            <address data-testid="footer-address" className="not-italic text-sm text-[#A1A1AA] leading-relaxed break-words">
              <div className="flex items-start gap-2">
                <MapPin size={14} className="mt-1 text-[#C8A96A] shrink-0" />
                <span className="min-w-0 break-words">
                  {site?.address_line1 || "Uzungöl Mahallesi"}<br />
                  {site?.address_line2 || "Fatih Caddesi No:63/B, Çaykara / Trabzon / Türkiye"}
                </span>
              </div>
              <a href={`tel:${phone.replace(/\s/g, "")}`} className="block mt-3 hover:text-white break-words">{phone}</a>
              <a href={`mailto:${site?.email || "info@uzungolkuyumculuk.com"}`} className="block mt-1 hover:text-white break-all">{site?.email || "info@uzungolkuyumculuk.com"}</a>
            </address>
          </div>
        </div>
        <div className="border-t border-[#27272A]">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-6 flex flex-col md:flex-row justify-between text-xs text-[#71717A] gap-2">
            <span>© {new Date().getFullYear()} Uzungöl Kuyumculuk. {lang === "tr" ? "Tüm hakları saklıdır." : "All rights reserved."}</span>
            <span>{lang === "tr" ? "Kişisel Veriler · Gizlilik · KVKK" : "Privacy · Terms · Cookies"}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

function SubNav({ id, label, parentTo, open, setOpen, children }) {
  const isOpen = open === id;
  return (
    <div className="relative" onMouseEnter={() => setOpen(id)} data-testid={`subnav-${id}`}>
      <Link to={parentTo} className={`flex items-center gap-1 px-4 py-2 text-sm uppercase tracking-[0.18em] hover:text-[#C8A96A] transition-colors ${isOpen ? "text-[#C8A96A]" : "text-[#0A0A0A]"}`}
        data-testid={`subnav-parent-${id}`}>
        {label} <ChevronDown size={12} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </Link>
      {isOpen && (
        <div data-testid={`subnav-panel-${id}`}
          className="absolute top-full left-0 w-60 bg-white border border-border shadow-xl py-2 z-50 animate-fade-in-up">
          {children}
        </div>
      )}
    </div>
  );
}

function MobileSection({ label, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button onClick={() => setOpen(!open)} className="flex items-center justify-between w-full py-3 text-sm uppercase tracking-[0.15em]">
        {label} <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="pb-2">{children}</div>}
    </div>
  );
}
